package dev.lingvo.site.ds

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.css.Overflow
import com.varabyte.kobweb.compose.css.PointerEvents
import com.varabyte.kobweb.compose.css.WhiteSpace
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.modifiers.animation
import com.varabyte.kobweb.compose.ui.modifiers.attr
import com.varabyte.kobweb.compose.ui.modifiers.bottom
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.flexWrap
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.left
import com.varabyte.kobweb.compose.ui.modifiers.lineHeight
import com.varabyte.kobweb.compose.ui.modifiers.opacity
import com.varabyte.kobweb.compose.ui.modifiers.overflow
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.modifiers.pointerEvents
import com.varabyte.kobweb.compose.ui.modifiers.position
import com.varabyte.kobweb.compose.ui.modifiers.right
import com.varabyte.kobweb.compose.ui.modifiers.top
import com.varabyte.kobweb.compose.ui.modifiers.translateY
import com.varabyte.kobweb.compose.ui.modifiers.whiteSpace
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.animation.Keyframes
import com.varabyte.kobweb.silk.style.animation.toAnimation
import com.varabyte.kobweb.silk.style.base
import com.varabyte.kobweb.silk.style.toModifier
import org.jetbrains.compose.web.css.AnimationFillMode
import org.jetbrains.compose.web.css.AnimationTimingFunction
import org.jetbrains.compose.web.css.FlexWrap
import org.jetbrains.compose.web.css.Position
import org.jetbrains.compose.web.css.ms
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text

private val glyphs = listOf(
    "Hello",
    "你好",
    "مرحبا",
    "Привет",
    "こんにちは",
    "Olá",
    "नमस्ते",
    "안녕",
    "Γεια",
)

/**
 * The ambient multi-script field.
 *
 * It is texture, not content: the mask fades it out toward the lower-left so headlines, eyebrows and
 * navigation always sit on clean ink rather than on legible foreign words. Without the mask the
 * glyphs read as clutter competing with the copy.
 */
val GlyphMosaicStyle = CssStyle.base {
    Modifier
        .position(Position.Absolute)
        .top(0.px)
        .right(0.px)
        .bottom(0.px)
        .left(0.px)
        .padding(Space.LG)
        .overflow(Overflow.Hidden)
        .pointerEvents(PointerEvents.None)
        // The documented values, restored: DESIGN.md fixes 0.07 dark / 0.12 light, and they had
        // drifted to 0.20/0.26 — at which the glyphs are legible enough to be read rather than
        // felt, and `你好` crosses an empty state's paragraph at full stroke weight.
        // textSecondary rather than textMuted, because at these opacities the muted grey resolves
        // to almost exactly the surface it sits on.
        .color(colorMode.toSitePalette().textSecondary)
        .opacity(if (colorMode.isDark) 0.07 else 0.12)
        .styleModifier {
            // Wrapped rows spread over the full height instead of stacking at the top.
            property("align-content", "space-around")
            property("mask-image", GLYPH_MOSAIC_SIDE_FADE)
            property("-webkit-mask-image", GLYPH_MOSAIC_SIDE_FADE)
        }
}

/**
 * The mask that keeps the mosaic off the copy, for a copy column pinned to the left.
 *
 * Anchored right of centre: the marketing bands set their headline, eyebrow and navigation on the
 * left, and those must sit on clean ink.
 */
private const val GLYPH_MOSAIC_SIDE_FADE =
    "radial-gradient(92% 82% at 88% 54%, rgba(0,0,0,1) 0%, rgba(0,0,0,.72) 34%, " +
        "rgba(0,0,0,.26) 60%, rgba(0,0,0,0) 84%)"

/**
 * The same mask for a *centred* copy column — [LEmptyState] and anything else that centres.
 *
 * The left-anchored fade puts its densest region on the right half of a centred paragraph, which is
 * exactly where the text is. This one clears the middle instead and keeps the glyphs to the corners,
 * so the copy sits on clean ground whichever side it is on.
 */
private const val GLYPH_MOSAIC_CENTRE_FADE =
    "radial-gradient(78% 86% at 50% 50%, rgba(0,0,0,0) 0%, rgba(0,0,0,0) 38%, " +
        "rgba(0,0,0,.45) 66%, rgba(0,0,0,1) 100%)"

/** Which side of the surface the mosaic has to stay away from. */
enum class GlyphMosaicFocus {
    /** Copy pinned to the left — the marketing bands. */
    START,

    /** Copy centred in the surface — [LEmptyState]. */
    CENTER,
}

/**
 * Applied inline rather than as a second [CssStyle], deliberately: two `CssStyle.base` rules carry
 * the same specificity, so which mask won would be decided by the order Kobweb happened to register
 * them in. An inline declaration beats both and cannot be reordered out from under this.
 */
private fun GlyphMosaicFocus.maskModifier(): Modifier = when (this) {
    GlyphMosaicFocus.START -> Modifier
    GlyphMosaicFocus.CENTER -> Modifier.styleModifier {
        property("mask-image", GLYPH_MOSAIC_CENTRE_FADE)
        property("-webkit-mask-image", GLYPH_MOSAIC_CENTRE_FADE)
    }
}

val GlyphMosaicItemStyle = CssStyle.base {
    Modifier
        .displayFont()
        .fontWeight(500)
        .lineHeight(TypeScale.DisplayLineHeight)
        .whiteSpace(WhiteSpace.NoWrap)
}

/**
 * The one orchestrated moment in the product, per DESIGN.md — the mosaic settling behind the hero.
 *
 * Transform only, and `Forwards` rather than `Both`, for the same reason as [LReveal]: a browser
 * that is not painting a tab does not advance the animation clock, so an opacity fade leaves the
 * element stranded on frame zero. That is survivable for text — it eventually appears — but the
 * mosaic is the page's atmosphere, and a hero that is simply blank until the tab is focused is not
 * the first impression. Sliding it in leaves it visible at its resting opacity throughout.
 */
val GlyphMosaicSettle = Keyframes {
    from {
        Modifier.translateY(Space.LG)
    }
    to {
        Modifier.translateY(0.px)
    }
}

@Composable
fun GlyphMosaic(
    modifier: Modifier = Modifier,
    animated: Boolean = false,
    focus: GlyphMosaicFocus = GlyphMosaicFocus.START,
) {
    val animationModifier = if (animated) {
        Modifier.animation(
            GlyphMosaicSettle.toAnimation(
                duration = Motion.DeliberateMs.ms,
                timingFunction = Motion.EaseEmphasizedTiming,
                fillMode = AnimationFillMode.Forwards,
            ),
        )
    } else {
        Modifier
    }

    Row(
        GlyphMosaicStyle.toModifier()
            .then(animationModifier)
            .then(focus.maskModifier())
            .attr("aria-hidden", "true")
            .then(modifier),
    ) {
        Row(
            Modifier
                .gap(rowGap = Space.XXL, columnGap = Space.XXXL)
                .flexWrap(FlexWrap.Wrap),
        ) {
            repeat(3) { pass ->
                glyphs.forEachIndexed { index, glyph ->
                    val size = when ((index + pass) % 4) {
                        0 -> TypeScale.Heading
                        1 -> TypeScale.HeadingSmall
                        2 -> TypeScale.Lead
                        else -> TypeScale.Display
                    }
                    Span(GlyphMosaicItemStyle.toModifier().fontSize(size).toAttrs()) {
                        Text(glyph)
                    }
                }
            }
        }
    }
}
